import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, User, Crown, UserCircle, Moon, FileText, Orbit } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

export default function UserMenu() {
  const { user, loading, loginWithGoogle, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) return null;

  if (!user) {
    return (
      <Button
        onClick={loginWithGoogle}
        className="bg-white/10 hover:bg-white/15 text-white/80 border border-white/10 rounded-full px-4 h-8 text-xs tracking-wider gap-2"
        data-testid="login-btn"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
        Sign in
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 h-8 px-2 rounded-full hover:bg-white/5 transition-colors"
          data-testid="user-menu-trigger"
        >
          {user.picture ? (
            <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
              <User className="w-3 h-3 text-white/50" />
            </div>
          )}
          <span className="text-white/70 text-xs tracking-wider max-w-[100px] truncate hidden sm:inline">
            {user.name?.split(" ")[0] || user.email?.split("@")[0]}
          </span>
          {user.is_premium && <Crown className="w-3 h-3 text-amber-400" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-[#0A0A0A] border-white/10 text-white/80 min-w-[180px]"
      >
        <div className="px-3 py-2">
          <p className="text-white/90 text-sm font-medium">{user.name}</p>
          <p className="text-white/40 text-xs">{user.email}</p>
          {user.is_premium && (
            <div className="flex items-center gap-1 mt-1">
              <Crown className="w-3 h-3 text-amber-400" />
              <span className="text-amber-400 text-[10px] tracking-wider uppercase">Premium</span>
            </div>
          )}
        </div>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem
          className="text-white/60 hover:text-white hover:bg-white/5 cursor-pointer text-xs"
          onClick={() => navigate("/daily-activation")}
          data-testid="menu-daily-activation-btn"
        >
          <Moon className="w-3.5 h-3.5 mr-2 text-amber-400/70" />
          Daily Chart Activation
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-white/60 hover:text-white hover:bg-white/5 cursor-pointer text-xs"
          onClick={() => navigate("/profile")}
          data-testid="menu-profile-btn"
        >
          <UserCircle className="w-3.5 h-3.5 mr-2" />
          My Profile
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-white/60 hover:text-white hover:bg-white/5 cursor-pointer text-xs"
          onClick={() => navigate("/karmic")}
          data-testid="menu-karmic-btn"
        >
          <Orbit className="w-3.5 h-3.5 mr-2 text-amber-400/70" />
          Karmic Timeline
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-white/60 hover:text-white hover:bg-white/5 cursor-pointer text-xs"
          onClick={() => navigate("/my-reports")}
          data-testid="menu-my-reports-btn"
        >
          <FileText className="w-3.5 h-3.5 mr-2" />
          My Reports
        </DropdownMenuItem>
        {!user.is_premium && (
          <DropdownMenuItem
            className="text-white/60 hover:text-white hover:bg-white/5 cursor-pointer text-xs"
            onClick={() => navigate("/pricing")}
            data-testid="menu-upgrade-btn"
          >
            <Crown className="w-3.5 h-3.5 mr-2 text-amber-400" />
            Upgrade to Premium
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className="text-white/60 hover:text-white hover:bg-white/5 cursor-pointer text-xs"
          onClick={logout}
          data-testid="menu-logout-btn"
        >
          <LogOut className="w-3.5 h-3.5 mr-2" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
